<script setup>
import { ref, computed, watch } from 'vue'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Settings } from 'lucide-vue-next'
import { updateReportSettings } from '@/services/reportApi'

const props = defineProps({
  report: {
    type: Object,
    required: true
  },
  organizationId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['report-updated'])

const isOpen = ref(false)
const isUpdating = ref(false)
const localIsPublic = ref(props.report?.is_public ?? false)

watch(() => props.report?.is_public, (newValue) => {
  localIsPublic.value = newValue ?? false
})

watch(isOpen, (newValue) => {
  if (newValue) {
    localIsPublic.value = props.report?.is_public ?? false
  }
})

const handlePublicToggle = async (value) => {
  if (isUpdating.value) return

  isUpdating.value = true
  try {
    const updatedReport = await updateReportSettings(
      props.organizationId,
      props.report.report_uuid,
      { is_public: value }
    )
    localIsPublic.value = value
    emit('report-updated', updatedReport)
  } catch (error) {
    console.error('Failed to update report settings:', error)
    localIsPublic.value = props.report?.is_public ?? false
  } finally {
    isUpdating.value = false
  }
}
</script>

<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <Button variant="ghost" size="icon" class="h-7 w-7">
        <Settings class="h-4 w-4" />
      </Button>
    </DialogTrigger>
    <DialogContent class="max-w-[350px]">
      <DialogHeader>
        <DialogTitle>Report Settings</DialogTitle>
      </DialogHeader>
      <div class="py-4">
        <div class="flex items-center justify-between">
          <div class="space-y-0.5">
            <Label class="text-sm font-medium">Public</Label>
            <p class="text-xs text-muted-foreground">
              Make this report visible to all organization members
            </p>
          </div>
          <Switch
            :model-value="localIsPublic"
            :disabled="isUpdating"
            @update:model-value="handlePublicToggle"
          />
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
